import { type SettingsStructure } from "@client/settings/structure";
import {
  type EntityRef,
  type GenericMap,
  type MainSettings,
  type ModuleConfig,
  type SettingsDefinitionTree,
  type WithId
} from "@client/types";
import { getSettingsStructure } from "../settings/module";
import * as AiChat from "./modules/AiChat";
import * as Audio from "./modules/Audio";
import * as BlogDetailComments from "./modules/BlogDetailComments";
import * as BlogDetailContent from "./modules/BlogDetailContent";
import * as BlogDetailHeader from "./modules/BlogDetailHeader";
import * as BlogList from "./modules/BlogList";
import * as Button from "./modules/Button";
import * as Cart from "./modules/Cart";
import * as Checkout from "./modules/Checkout";
import * as CheckoutButton from "./modules/CheckoutButton";
import * as ContactForm from "./modules/ContactForm";
import * as Countdown from "./modules/Countdown";
import * as CountdownTimers from "./modules/CountdownTimers";
import * as Counter from "./modules/Counter";
import * as CourseDetail from "./modules/CourseDetail";
import * as CourseList from "./modules/CourseList";
import * as CourseProgress from "./modules/CourseProgress";
import * as Events from "./modules/Events";
import * as Facebook from "./modules/Facebook";
import * as ForgotPassword from "./modules/ForgotPassword";
import * as Form from "./modules/Form";
import * as Forums from "./modules/Forums";
import * as HTML from "./modules/HTML";
import * as Icon from "./modules/Icon";
import * as IconBoxes from "./modules/IconBoxes";
import * as Image from "./modules/Image";
import * as ImageGallery from "./modules/ImageGallery";
import * as Instagram from "./modules/Instagram";
import * as KnowledgeBase from "./modules/KnowledgeBase";
import * as Kpi from "./modules/Kpi";
import * as Leaderboard from "./modules/Leaderboard";
import * as Lists from "./modules/Lists";
import * as LoginForm from "./modules/LoginForm";
import * as Maps from "./modules/Maps";
import * as MemberAffiliate from "./modules/MemberAffiliate";
import * as MemberAffiliateDashboard from "./modules/MemberAffiliateDashboard";
import * as MemberDownloads from "./modules/MemberDownloads";
import * as MemberHelpdesk from "./modules/MemberHelpdesk";
import * as MemberOrders from "./modules/MemberOrders";
import * as MemberPayoutMethods from "./modules/MemberPayoutMethods";
import * as MemberProfile from "./modules/MemberProfile";
import * as MemberRecurringOrders from "./modules/MemberRecurringOrders";
import * as MemberSurvey from "./modules/MemberSurvey";
import * as MemberTicketDetail from "./modules/MemberTicketDetail";
import * as MemberVouchers from "./modules/MemberVouchers";
import * as MemberWallets from "./modules/MemberWallets";
import * as MiniProfile from "./modules/MiniProfile";
import * as NetworkPlacement from "./modules/NetworkPlacement";
import * as OptInFormV2 from "./modules/OptInFormV2";
import * as OrderConfirmation from "./modules/OrderConfirmation";
import * as OrderDetail from "./modules/OrderDetail";
import * as OrderTimeline from "./modules/OrderTimeline";
import * as PackagePricing from "./modules/PackagePricing";
import * as PieChart from "./modules/PieChart";
import * as PortfolioGallery from "./modules/PortfolioGallery";
import * as ProductDetail from "./modules/ProductDetail";
import * as ProductList from "./modules/ProductList";
import * as ProgressBar from "./modules/ProgressBar";
import * as Purchase from "./modules/Purchase";
import * as Register from "./modules/Register";
import * as Report from "./modules/Report";
import * as Search from "./modules/Search";
import * as Separators from "./modules/Separators";
import * as SocialIcons from "./modules/SocialIcons";
import * as Spacer from "./modules/Spacer";
import * as SystemRedirect from "./modules/SystemRedirect";
import * as TextHTML from "./modules/TextHTML";
import * as Title from "./modules/Title";
import * as Twitter from "./modules/Twitter";
import * as Unsubscribe from "./modules/Unsubscribe";
import * as Verification from "./modules/Verification";
import * as Video from "./modules/Video";
import { type RootContainerComponent } from "./types";

const moduleMap: GenericMap<ModuleExportData> = {
  AiChat,
  Audio,
  BlogDetailComments,
  BlogDetailContent,
  BlogDetailHeader,
  BlogList,
  Button,
  Cart,
  Checkout,
  CheckoutButton,
  ContactForm,
  Countdown,
  CountdownTimers,
  Counter,
  CourseDetail,
  CourseList,
  CourseProgress,
  Events,
  Facebook,
  ForgotPassword,
  Form,
  Forums,
  HTML,
  Icon,
  IconBoxes,
  Image,
  ImageGallery,
  Instagram,
  KnowledgeBase,
  Kpi,
  Leaderboard,
  Lists,
  LoginForm,
  Maps,
  MemberAffiliate,
  MemberAffiliateDashboard,
  MemberDownloads,
  MemberHelpdesk,
  MemberOrders,
  MemberPayoutMethods,
  MemberProfile,
  MemberRecurringOrders,
  MemberSurvey,
  MemberTicketDetail,
  MemberVouchers,
  MemberWallets,
  MiniProfile,
  NetworkPlacement,
  OptInFormV2,
  OrderConfirmation,
  OrderDetail,
  OrderTimeline,
  PackagePricing,
  PieChart,
  PortfolioGallery,
  ProductDetail,
  ProductList,
  ProgressBar,
  Purchase,
  Register,
  Report,
  Search,
  Separators,
  SocialIcons,
  Spacer,
  SystemRedirect,
  TextHTML,
  Title,
  Twitter,
  Unsubscribe,
  Verification,
  Video
};

export type ModuleInfo = {
  data: ModuleExportData | null;

  isObsolete?: boolean;

  config: Omit<ModuleConfig, "item">;
  structure?: SettingsStructure;
  definitions?: SettingsDefinitionTree;
  label: string;
  hostOnly?: boolean;
  imageUrl: string;
  isSingleItem: boolean;
  subscription?: string;
  type: string;
};

export type ModuleConfigureArgs = WithId & {};

export type ModuleConfigureResult = {
  settings?: MainSettings;
};

export interface ModuleExportData {
  Container: RootContainerComponent;
  layout?: SettingsStructure;
  getReducer?: (id: string) => any; // TODO: TS
  init?: (args: EntityRef) => void;
}

const modules: GenericMap<ModuleInfo> = Object.keys(moduleMap).reduce<GenericMap<ModuleInfo>>((res, name) => {
  const { config, commonStructure, definitions, itemsStructure } = getSettingsStructure(name);

  const moduleInfo: ModuleInfo = {
    config: {
      ...config,
      items: itemsStructure ?? undefined
    },
    definitions: definitions ?? undefined,
    structure: commonStructure ?? undefined,
    hostOnly: config.hostOnly,
    // TODO: improve. Multi-item modules are supposed to have settings + item definitions
    isSingleItem: !config.item && !config.items,
    label: config.label,
    imageUrl: config.imageUrl || `#wsc-${name.toLowerCase()}-module`,
    isObsolete: config.obsolete,
    type: name,
    data: moduleMap[name],
    subscription: config.subscription
  };
  return {
    ...res,
    [name]: moduleInfo
  };
}, {});

export const getModuleInfo = (type: string): ModuleInfo => {
  if (!(type in modules)) {
    return {
      config: { label: "NA" },
      data: null,
      imageUrl: "",
      isSingleItem: false,
      label: "NA",
      type
    };
  }
  return modules[type];
};

export const getModuleData = (type: string): ModuleExportData | null => {
  return getModuleInfo(type).data;
};

export const getAllModulesDisplayInfo = (): ModuleInfo[] => {
  return Object.values(modules);
};

export const getModuleLabel = (type: string): string => {
  const { label, isObsolete } = getModuleInfo(type);
  return `${label}${isObsolete ? " (obsolete)" : ""}`;
};
